import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import styles from "./styles.module.scss";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import TextInput, {
  InputType,
} from "../../../../../../components/forms/textInput";
import {
  isValidSaudiPhoneNumber,
  normalizeSaudiNumber,
} from "../../../../../../setup/util/phoneNumber.helpers";
import Modal from "../../../../../../components/common/Modal";
import Spinner from "../../../../../../components/common/Spinner";

export type Inputs = {
  id: number;
  name: string;
  phoneNumber: string;
  email: string;
  permissions: string[];
};

interface IAddUserModalProps {
  modalOpen: boolean;
  loading: boolean;
  handleClose: () => void;
  handleSave: (data: Inputs) => void;
  mode: "add" | "edit";
  initialData?: Inputs | null;
}

export const PERMISSION_OPTIONS = [
  { id: "CLIENT_ADMIN", label: "كل الصلاحيات" },
  { id: "CLIENT_ADMIN_CREATE_CONTRACT", label: "إنشاء مشروع" },
  { id: "CLIENT_ADMIN_UPDATE_CONTRACT", label: "تعديل مشروع" },
  { id: "CLIENT_ADMIN_VIEW_CONTRACT", label: "عرض المشاريع" },
  { id: "CLIENT_ADMIN_CREATE_USER", label: "إضافة مسئول" },
];

export default function AddUserModal({
  modalOpen,
  handleClose,
  handleSave,
  mode,
  initialData,
  loading,
}: IAddUserModalProps) {
  const { t } = useTranslation();

  const schema = z.object({
    id: z.number(),
    name: z
      .string()
      .min(3, t("err_min_chars", { min: 3 }))
      .max(100, t("err_max_chars", { max: 100 })),
    phoneNumber: z.string().refine((v) => isValidSaudiPhoneNumber(v), {
      message: t("err_invalid_phone"),
    }),
    email: z.string().email(t("err_invalid_email")),
    permissions: z.array(z.string()),
  });

  const {
    control,
    reset,
    watch,
    handleSubmit,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<Inputs>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      id: 0,
      name: "",
      phoneNumber: "",
      email: "",
      permissions: ["CLIENT_ADMIN_VIEW_CONTRACT"],
    },
  });

  useEffect(() => {
    if (mode === "edit" && initialData) {
      reset(initialData);
    } else {
      reset({
        id: 0,
        name: "",
        phoneNumber: "",
        email: "",
        permissions: ["CLIENT_ADMIN_VIEW_CONTRACT"],
      });
    }
  }, [mode, initialData, reset]);

  const togglePermission = (permId: string) => {
    const current = watch("permissions") || [];

    // Special handling for CLIENT_ADMIN permission
    if (permId === "CLIENT_ADMIN") {
      if (current.includes(permId)) {
        // If CLIENT_ADMIN is being deselected, deselect all permissions
        setValue("permissions", [], {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        // If CLIENT_ADMIN is being selected, select all permissions
        const allPermissionIds = PERMISSION_OPTIONS.map((perm) => perm.id);
        setValue("permissions", allPermissionIds, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      return;
    }

    // Regular toggle behavior for other permissions
    if (current.includes(permId)) {
      setValue(
        "permissions",
        current.filter((p) => p !== permId),
        { shouldValidate: true, shouldDirty: true }
      );
    } else {
      setValue("permissions", [...current, permId], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  return (
    <Modal
      visible={modalOpen}
      onCancel={() => {
        handleClose();
        reset();
      }}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}>
      <div className={styles.container}>
        <h1 className={styles.title}>
          {mode === "edit" ? t("lbl_edit_user") : t("lbl_add_user")}
        </h1>

        {/* NAME */}
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              disabled={mode === "edit"}
              placeholder={t("lbl_name")}
              errorMessage={errors?.name?.message}
              required
            />
          )}
        />

        {/* PHONE NUMBER */}
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <TextInput
              style={{ direction: "ltr" }}
              {...field}
              disabled={mode === "edit"}
              placeholder={t("lbl_mobile_number")}
              type={InputType.PHONE_NUMBER}
              errorMessage={errors?.phoneNumber?.message}
              required
              onChange={(e) => {
                const v = e.target.value.replace(/\s+/g, "");
                field.onChange(normalizeSaudiNumber(v));
              }}
            />
          )}
        />

        {/* EMAIL */}
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              disabled={mode === "edit"}
              placeholder={t("email")}
              errorMessage={errors?.email?.message}
              required
            />
          )}
        />

        {/* PERMISSIONS */}
        <div className={styles.permissionsWrapper}>
          <h2 className={styles.permissionsTitle}>{t("lbl_permissions")}</h2>

          {PERMISSION_OPTIONS.map((perm) => (
            <label key={perm.id} className={styles.permissionRow}>
              <input
                type="checkbox"
                checked={watch("permissions")?.includes(perm.id)}
                onChange={() => togglePermission(perm.id)}
              />
              <span>{perm.label}</span>
            </label>
          ))}
        </div>

        <div className={styles.submitButton}>
          <PrimaryButton
            disabled={mode === "edit" ? !isDirty || !isValid : !isValid}
            style={{
              background:
                mode === "edit"
                  ? isValid && isDirty
                    ? "#0D44AD"
                    : "#EEF0F5"
                  : isValid
                  ? "#0D44AD"
                  : "#EEF0F5",
            }}
            onClick={(data) => {
              handleSubmit(handleSave)(data);
            }}>
            {mode === "add" && !loading ? (
              t("lbl_slider_add_bank_btn")
            ) : mode === "edit" && !loading ? (
              t("lbl_edit")
            ) : (
              <Spinner />
            )}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
